import { supabase } from "../supabase";

// ---------------------------------------------------------------------------
// Audit share tokens. Mirrors the invite-token pattern (0009): the raw
// token only lives in the URL, the DB stores SHA-256(raw). The share URL
// is /audit-share/<raw>.
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  const b64 = btoa(bin);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function toHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 1) out += bytes[i].toString(16).padStart(2, "0");
  return out;
}

export async function hashAuditShareToken(raw: string): Promise<string> {
  const enc = new TextEncoder();
  const digest = await crypto.subtle.digest("SHA-256", enc.encode(raw));
  return toHex(new Uint8Array(digest));
}

export async function generateAuditShareToken(): Promise<{ raw: string; hash: string; }> {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const raw = base64UrlEncode(bytes);
  const hash = await hashAuditShareToken(raw);
  return { raw, hash };
}

export interface AuditShareScope {
  dateFrom?: string;
  dateTo?: string;
  types?: Array<"expenses" | "invoices">;
}

export interface CreateAuditShareInput {
  orgId: string;
  name: string;
  expiresAt: string;
  scope: AuditShareScope;
}

export interface CreateAuditShareResult {
  id: string;
  raw: string;
  url: string;
}

export async function createAuditShare(input: CreateAuditShareInput): Promise<CreateAuditShareResult> {
  const { raw, hash } = await generateAuditShareToken();
  const origin = typeof window !== "undefined" ? window.location.origin : "https://proofly.one";
  const url = `${origin}/audit-share/${raw}`;
  const { data, error } = await supabase.rpc("create_audit_share", {
    org: input.orgId,
    share_name: input.name,
    scope_json: input.scope,
    expires_at: input.expiresAt,
    token_hash: hash,
  });
  if (error) throw new Error(error.message);
  return { id: String(data ?? ""), raw, url };
}

export async function revokeAuditShare(shareId: string): Promise<void> {
  const { error } = await supabase.rpc("revoke_audit_share", { share_id: shareId });
  if (error) throw new Error(error.message);
}
